import { useState, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeftIcon, CameraIcon } from '@heroicons/react/20/solid'
import { CheckCircleIcon, ExclamationCircleIcon, CloudArrowDownIcon, KeyIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline'
import { useAuthStatus } from './user_dropdown'
import { PilotAvatar } from './pilot_avatar'
import { Button } from '../components/button'
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '../components/dialog'
import { Field, FieldGroup, Fieldset, Label, Description } from '../components/fieldset'
import { Input } from '../components/input'
import { syrideLinkPayload } from './syride_account'

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'
type ImportStatus = 'idle' | 'submitting' | 'queued' | 'error'

export function UserSettings() {
  const { userData, loading } = useAuthStatus()
  const navigate = useNavigate()

  const pilot = userData.pilot

  const [nick, setNick] = useState(pilot?.nick ?? '')
  const [nickStatus, setNickStatus] = useState<SaveStatus>('idle')
  const [nickError, setNickError] = useState('')

  const [name, setName] = useState(pilot?.name ?? '')
  const [nameStatus, setNameStatus] = useState<SaveStatus>('idle')

  const [xcLogin, setXcLogin] = useState(pilot?.xcontest_login ?? '')
  const [xcPassword, setXcPassword] = useState('')
  const [credStatus, setCredStatus] = useState<SaveStatus>('idle')

  const [picStatus, setPicStatus] = useState<SaveStatus>('idle')
  const [profilePictureId, setProfilePictureId] = useState(pilot?.profile_picture_id ?? null)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [xcImportStatus, setXcImportStatus] = useState<ImportStatus>('idle')

  const [syridePilot, setSyridePilot] = useState(pilot?.syride_pilot ?? '')
  const [syrideLogin, setSyrideLogin] = useState(pilot?.syride_login ?? '')
  const [syridePassword, setSyridePassword] = useState('')
  const [syrideLinked, setSyrideLinked] = useState(Boolean(pilot?.syride_login))
  const [importStatus, setImportStatus] = useState<ImportStatus>('idle')

  const [tokenModalOpen, setTokenModalOpen] = useState(false)
  const [generatedToken, setGeneratedToken] = useState('')
  const [tokenGenerating, setTokenGenerating] = useState(false)
  const [tokenCopied, setTokenCopied] = useState(false)

  // Sync login field once pilot data arrives
  const [syncedLogin, setSyncedLogin] = useState(false)
  if (pilot && !syncedLogin) {
    setNick(pilot.nick ?? '')
    setName(pilot.name ?? '')
    setXcLogin(pilot.xcontest_login ?? '')
    setSyridePilot(pilot.syride_pilot ?? '')
    setSyrideLogin(pilot.syride_login ?? '')
    setSyrideLinked(Boolean(pilot.syride_login))
    setProfilePictureId(pilot.profile_picture_id ?? null)
    setSyncedLogin(true)
  }

  const currentPilot = { ...pilot, profile_picture_id: profilePictureId }

  const uploadProfilePicture = useCallback(async (file: File) => {
    setUploading(true)
    setPicStatus('idle')
    try {
      const form = new FormData()
      form.append('file', file)
      const uploadRes = await fetch('/v0/media', { method: 'POST', body: form })
      if (!uploadRes.ok) throw new Error('Upload failed')
      const media = await uploadRes.json()
      const newId: string = media.id

      const patchRes = await fetch('/v0/me', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profile_picture_id: newId }),
      })
      if (!patchRes.ok) throw new Error('Failed to update profile')

      setProfilePictureId(newId)
      setPicStatus('saved')
    } catch {
      setPicStatus('error')
    } finally {
      setUploading(false)
    }
  }, [])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) uploadProfilePicture(file)
  }

  const validNick = /^[a-zA-Z0-9._-]+$/

  const saveNick = async () => {
    if (nick === pilot?.nick) return
    if (!nick) { setNickError('Nickname cannot be empty'); return }
    if (!validNick.test(nick)) { setNickError('Only letters, numbers, and . - _ are allowed'); return }
    setNickError('')
    setNickStatus('saving')
    try {
      const res = await fetch('/v0/me', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nick }),
      })
      if (res.status === 409) { setNickError('This nickname is already taken'); setNickStatus('idle'); return }
      if (!res.ok) throw new Error('Save failed')
      setNickStatus('saved')
    } catch {
      setNickStatus('error')
    }
  }

  const saveName = async () => {
    if (name === pilot?.name) return
    setNameStatus('saving')
    try {
      const res = await fetch('/v0/me', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      })
      if (!res.ok) throw new Error('Save failed')
      setNameStatus('saved')
    } catch {
      setNameStatus('error')
    }
  }

  const saveCredentials = async () => {
    setCredStatus('saving')
    try {
      const body: Record<string, string> = {}
      if (xcLogin !== (pilot?.xcontest_login ?? '')) body.xcontest_login = xcLogin
      if (xcPassword) body.xcontest_password = xcPassword
      if (Object.keys(body).length === 0) { setCredStatus('saved'); return }

      const res = await fetch('/v0/me', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!res.ok) throw new Error('Save failed')
      setXcPassword('')
      setCredStatus('saved')
    } catch {
      setCredStatus('error')
    }
  }

  const triggerXContestImport = async () => {
    setXcImportStatus('submitting')
    try {
      const res = await fetch('/v0/me/xcontest-import', { method: 'POST' })
      if (!res.ok) throw new Error('Import request failed')
      setXcImportStatus('queued')
    } catch {
      setXcImportStatus('error')
    }
  }

  const triggerSyrideCrawl = async () => {
    if (!syridePilot.trim() || !syrideLogin.trim() || (!syridePassword && !syrideLinked)) return
    setImportStatus('submitting')
    try {
      const res = await fetch('/v0/me/syride-crawl', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(syrideLinkPayload(syridePilot, syrideLogin, syridePassword)),
      })
      if (!res.ok) throw new Error('Import request failed')
      setSyrideLinked(true)
      setSyridePassword('')
      setImportStatus('queued')
    } catch {
      setImportStatus('error')
    }
  }

  const generateAPIToken = async () => {
    setTokenGenerating(true)
    setTokenCopied(false)
    try {
      const res = await fetch('/v0/me/api-token', { method: 'POST' })
      if (!res.ok) throw new Error('Token generation failed')
      const data = await res.json()
      setGeneratedToken(data.token)
      setTokenModalOpen(true)
    } catch {
      setGeneratedToken('')
    } finally {
      setTokenGenerating(false)
    }
  }

  const copyTokenToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedToken)
      setTokenCopied(true)
    } catch {
      // Fallback: select the text in the input
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-[#4F5D75] dark:text-[#BFC0C0]">
        Loading…
      </div>
    )
  }

  if (!userData.authenticated || !pilot) {
    return (
      <div className="flex items-center justify-center h-64 text-[#4F5D75] dark:text-[#BFC0C0]">
        You must be signed in to access settings.
      </div>
    )
  }

  return (
    <div className="max-w-xl mx-auto py-10 px-4">
      <div className="mb-8">
        <Button plain onClick={() => navigate(-1)}>
          <ArrowLeftIcon />
          Back
        </Button>
      </div>

      <h1 className="text-2xl font-bold text-[#2D3142] dark:text-white mb-8">Settings</h1>

      {/* Profile picture */}
      <section className="mb-10 p-6 rounded-xl bg-white dark:bg-[#2D3142]/40 border border-[#BFC0C0]/20 shadow-sm">
        <h2 className="text-base font-semibold text-[#2D3142] dark:text-white mb-4">Profile picture</h2>

        <div className="flex items-center gap-5">
          <div className="relative group">
            <PilotAvatar
              pilot={currentPilot}
              className="size-20 bg-[#4F5D75]/20 text-[#4F5D75] dark:bg-[#BFC0C0]/20 dark:text-[#BFC0C0]"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="absolute inset-0 rounded-full flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer disabled:cursor-not-allowed"
              aria-label="Change profile picture"
            >
              <CameraIcon className="size-6 text-white" />
            </button>
          </div>

          <div className="flex flex-col gap-2">
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              outline
            >
              {uploading ? 'Uploading…' : 'Upload photo'}
            </Button>
            <p className="text-xs text-[#4F5D75] dark:text-[#BFC0C0]">JPG, PNG or WebP. Max 10 MB.</p>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>

        <StatusMessage status={picStatus} />
      </section>

      {/* Nickname */}
      <section className="mt-6 p-6 rounded-xl bg-white dark:bg-[#2D3142]/40 border border-[#BFC0C0]/20 shadow-sm">
        <h2 className="text-base font-semibold text-[#2D3142] dark:text-white mb-1">Nickname</h2>
        <p className="text-sm text-[#4F5D75] dark:text-[#BFC0C0] mb-5">
          Your public handle on Airtime. Letters, numbers, and <code className="font-mono">. - _</code> only.
        </p>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Nickname</Label>
              {nickError && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-1">{nickError}</p>
              )}
              <Input
                type="text"
                value={nick}
                onChange={e => { setNick(e.target.value); setNickStatus('idle'); setNickError('') }}
                autoComplete="off"
              />
            </Field>
          </FieldGroup>
        </Fieldset>
        <div className="mt-6 flex items-center gap-4">
          <Button
            onClick={saveNick}
            disabled={nickStatus === 'saving' || nick === pilot.nick}
            color="orange"
          >
            {nickStatus === 'saving' ? 'Saving…' : 'Save nickname'}
          </Button>
          <StatusMessage status={nickStatus} />
        </div>
      </section>

      {/* Display name */}
      <section className="mt-6 p-6 rounded-xl bg-white dark:bg-[#2D3142]/40 border border-[#BFC0C0]/20 shadow-sm">
        <h2 className="text-base font-semibold text-[#2D3142] dark:text-white mb-1">Display name</h2>
        <p className="text-sm text-[#4F5D75] dark:text-[#BFC0C0] mb-5">
          Your full name shown on your profile and flights.
        </p>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Name</Label>
              <Input
                type="text"
                value={name}
                onChange={e => { setName(e.target.value); setNameStatus('idle') }}
                autoComplete="name"
              />
            </Field>
          </FieldGroup>
        </Fieldset>
        <div className="mt-6 flex items-center gap-4">
          <Button
            onClick={saveName}
            disabled={nameStatus === 'saving' || name === pilot.name}
            color="orange"
          >
            {nameStatus === 'saving' ? 'Saving…' : 'Save name'}
          </Button>
          <StatusMessage status={nameStatus} />
        </div>
      </section>

      {/* XContest credentials */}
      <section className="p-6 rounded-xl bg-white dark:bg-[#2D3142]/40 border border-[#BFC0C0]/20 shadow-sm">
        <h2 className="text-base font-semibold text-[#2D3142] dark:text-white mb-1">XContest credentials</h2>
        <p className="text-sm text-[#4F5D75] dark:text-[#BFC0C0] mb-5">
          Used to synchronise your flights with XContest automatically.
        </p>

        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Username</Label>
              <Input
                type="text"
                value={xcLogin}
                onChange={e => { setXcLogin(e.target.value); setCredStatus('idle') }}
                placeholder="Your XContest username"
                autoComplete="off"
              />
            </Field>
            <Field>
              <Label>Password</Label>
              <Input
                type="password"
                value={xcPassword}
                onChange={e => { setXcPassword(e.target.value); setCredStatus('idle') }}
                placeholder={pilot.xcontest_login ? '••••••••' : 'Your XContest password'}
                autoComplete="new-password"
              />
            </Field>
          </FieldGroup>
        </Fieldset>

        <div className="mt-6 flex items-center gap-4">
          <Button
            onClick={saveCredentials}
            disabled={credStatus === 'saving'}
            color="orange"
          >
            {credStatus === 'saving' ? 'Saving…' : 'Save credentials'}
          </Button>
          <StatusMessage status={credStatus} />
        </div>

        {pilot.xcontest_login && (
          <div className="mt-4 pt-4 border-t border-[#BFC0C0]/20 flex items-center gap-4">
            <Button
              onClick={triggerXContestImport}
              disabled={xcImportStatus === 'submitting'}
              outline
            >
              <CloudArrowDownIcon className="size-4" />
              {xcImportStatus === 'submitting' ? 'Requesting…' : 'Import from XContest'}
            </Button>
            <ImportStatusMessage status={xcImportStatus} />
          </div>
        )}
      </section>

      {/* Syride import */}
      <section className="mt-10 p-6 rounded-xl bg-white dark:bg-[#2D3142]/40 border border-[#BFC0C0]/20 shadow-sm">
        <h2 className="text-base font-semibold text-[#2D3142] dark:text-white mb-1">Syride account</h2>
        <p className="text-sm text-[#4F5D75] dark:text-[#BFC0C0] mb-5">
          Used to authenticate with Syride, import your flights, and synchronise them automatically.
        </p>

        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Profile username</Label>
              <Description>Your username on syride.com (visible in your profile URL).</Description>
              <Input
                type="text"
                value={syridePilot}
                onChange={e => { setSyridePilot(e.target.value); setImportStatus('idle') }}
                placeholder="e.g. john.doe"
                autoComplete="off"
              />
            </Field>
            <Field>
              <Label>Email</Label>
              <Input
                type="email"
                value={syrideLogin}
                onChange={e => { setSyrideLogin(e.target.value); setImportStatus('idle') }}
                placeholder="Your Syride account email"
                autoComplete="username"
              />
            </Field>
            <Field>
              <Label>Password</Label>
              <Input
                type="password"
                value={syridePassword}
                onChange={e => { setSyridePassword(e.target.value); setImportStatus('idle') }}
                placeholder={syrideLinked ? '••••••••' : 'Your Syride password'}
                autoComplete="current-password"
              />
            </Field>
          </FieldGroup>
        </Fieldset>

        <div className="mt-6 flex items-center gap-4">
          <Button
            onClick={triggerSyrideCrawl}
            disabled={
              !syridePilot.trim() ||
              !syrideLogin.trim() ||
              (!syridePassword && !syrideLinked) ||
              importStatus === 'submitting'
            }
            color="orange"
          >
            <CloudArrowDownIcon className="size-4" />
            {importStatus === 'submitting' ? 'Requesting…' : 'Link and import'}
          </Button>
          <ImportStatusMessage status={importStatus} />
        </div>
      </section>

      {/* API token */}
      <section className="mt-10 p-6 rounded-xl bg-white dark:bg-[#2D3142]/40 border border-[#BFC0C0]/20 shadow-sm">
        <h2 className="text-base font-semibold text-[#2D3142] dark:text-white mb-1">API token</h2>
        <p className="text-sm text-[#4F5D75] dark:text-[#BFC0C0] mb-5">
          Generate a personal token to authenticate API requests. Any previous token will be replaced.
        </p>

        <Button
          onClick={generateAPIToken}
          disabled={tokenGenerating}
          outline
        >
          <KeyIcon className="size-4" />
          {tokenGenerating ? 'Generating…' : 'Generate API token'}
        </Button>
      </section>

      {/* API token modal */}
      <Dialog open={tokenModalOpen} onClose={() => setTokenModalOpen(false)} size="md">
        <DialogTitle>Your new API token</DialogTitle>
        <DialogDescription>
          Copy this token now — it won't be shown again. Use it in the <code className="font-mono text-xs bg-zinc-100 dark:bg-zinc-800 px-1.5 py-0.5 rounded">Authorization</code> header as <code className="font-mono text-xs bg-zinc-100 dark:bg-zinc-800 px-1.5 py-0.5 rounded">AIRTIME {'<token>'}</code>.
        </DialogDescription>
        <DialogBody>
          <div className="flex items-center gap-2">
            <Input
              type="text"
              value={generatedToken}
              readOnly
              className="font-mono text-sm"
              onFocus={e => e.target.select()}
            />
            <Button onClick={copyTokenToClipboard} outline>
              <ClipboardDocumentIcon className="size-4" />
              {tokenCopied ? 'Copied!' : 'Copy'}
            </Button>
          </div>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setTokenModalOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

function StatusMessage({ status }: { status: SaveStatus }) {
  if (status === 'saved') {
    return (
      <span className="flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
        <CheckCircleIcon className="size-4" />
        Saved
      </span>
    )
  }
  if (status === 'error') {
    return (
      <span className="flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
        <ExclamationCircleIcon className="size-4" />
        Something went wrong
      </span>
    )
  }
  return null
}

function ImportStatusMessage({ status }: { status: ImportStatus }) {
  if (status === 'queued') {
    return (
      <span className="flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
        <CheckCircleIcon className="size-4" />
        Import started — flights will appear shortly
      </span>
    )
  }
  if (status === 'error') {
    return (
      <span className="flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
        <ExclamationCircleIcon className="size-4" />
        Failed to start import
      </span>
    )
  }
  return null
}
